import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiVideo,
  FiYoutube,
  FiUpload,
  FiLink,
  FiZap,
  FiCopy,
  FiDownload,
  FiX,
  FiChevronDown,
  FiCheck,
  FiAlertCircle,
  FiCode,
  FiEdit3,
  FiFileText,
  FiFile,
  FiRefreshCw,
} from 'react-icons/fi';
import { videoService, contentService, promptService } from '../services/apiService';
import { useAuthStore } from '../utils/store';
import { useT } from '../utils/i18n';

// ─── Step indicator ───────────────────────────────────────────────────────────

const STEP_IDS = ['upload', 'transcribe', 'generate'];

const StepIndicator = ({ currentStep, completedSteps, steps }) => (
  <div className="space-y-2">
    {steps.map((step, idx) => {
      const isDone    = completedSteps.has(STEP_IDS[idx]);
      const isActive  = STEP_IDS[idx] === currentStep && !isDone;

      return (
        <div key={STEP_IDS[idx]} className="flex items-center gap-3">
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
              isDone    ? 'bg-green-500'
              : isActive ? 'bg-primary-500'
              : 'bg-gray-200'
            }`}
          >
            {isDone ? (
              <FiCheck size={12} className="text-white" />
            ) : isActive ? (
              <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <span className="text-[10px] font-bold text-gray-400">{idx + 1}</span>
            )}
          </div>
          <span
            className={`text-sm transition-colors ${
              isDone    ? 'text-green-600 font-medium'
              : isActive ? 'text-primary-600 font-semibold'
              : 'text-gray-400'
            }`}
          >
            {step}
            {isActive && '…'}
          </span>
        </div>
      );
    })}
  </div>
);

// ─── Sub-tab bar ──────────────────────────────────────────────────────────────

const SubTabBar = ({ active, onChange, tabs }) => (
  <div className="flex border-b border-gray-200">
    {tabs.map(({ id, label }) => (
      <button
        key={id}
        onClick={() => onChange(id)}
        className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
          active === id
            ? 'border-primary-500 text-primary-600 bg-primary-50/40'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

// ─── Output panel (shared by both sub-tabs) ───────────────────────────────────

const OutputPanel = ({ text, label, onDownload, downloadDisabled }) => {
  const t = useT();
  const [copied, setCopied]     = useState(false);
  const [downloadOpen, setOpen] = useState(false);
  const dropRef                 = useRef(null);

  useEffect(() => {
    const h = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleCopy = async () => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const wc = text ? text.trim().split(/\s+/).filter(Boolean).length : 0;

  return (
    <div className="flex flex-col gap-2">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{label}</span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleCopy}
            disabled={!text}
            className="btn btn-ghost flex items-center gap-1.5 text-sm disabled:opacity-40"
          >
            {copied ? <FiCheck size={13} className="text-green-500" /> : <FiCopy size={13} />}
            <span>{copied ? t('genpage.copied') : t('genpage.copy')}</span>
          </button>
          <div className="relative" ref={dropRef}>
            <button
              onClick={() => setOpen((v) => !v)}
              disabled={!text || downloadDisabled}
              className="btn btn-ghost flex items-center gap-1.5 text-sm disabled:opacity-40"
            >
              <FiDownload size={13} />
              <span>{t('genpage.download')}</span>
              <FiChevronDown size={11} className={`text-gray-400 transition-transform ${downloadOpen ? 'rotate-180' : ''}`} />
            </button>
            {downloadOpen && (
              <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-20">
                <button
                  onClick={() => { onDownload('docx'); setOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                >
                  <FiFileText size={13} className="text-blue-500 flex-shrink-0" />
                  <span>{t('genpage.docx')}</span>
                </button>
                <button
                  onClick={() => { onDownload('pdf'); setOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                >
                  <FiFile size={13} className="text-red-500 flex-shrink-0" />
                  <span>{t('genpage.pdf')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Text area */}
      <textarea
        readOnly
        value={text}
        placeholder={t('videoToScript.willAppear')}
        className="input w-full resize-y font-sans text-sm leading-relaxed bg-gray-50/80 text-gray-800"
        style={{ minHeight: '340px' }}
      />
      {text && (
        <div className="flex justify-between text-xs text-gray-400">
          <span>{text.length} {t('genpage.characters')}</span>
          <span>{wc} {t('genpage.words')}</span>
        </div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const VideoToScript = () => {
  const t         = useT();
  const { user }  = useAuthStore();
  const navigate  = useNavigate();

  const [mode, setMode]           = useState('upload');
  const [file, setFile]           = useState(null);
  const [isDragging, setDragging] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [tone, setTone]           = useState('Formal');

  const [currentStep, setCurrentStep]       = useState(null);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [uploadProgress, setUploadProgress] = useState(0);

  const [transcript, setTranscript] = useState('');
  const [script, setScript]         = useState('');
  const [subTab, setSubTab]         = useState('TV Script');

  const [promptOpen, setPromptOpen]       = useState(false);
  const [promptData, setPromptData]       = useState(null);
  const [promptLoading, setPromptLoading] = useState(false);

  const [error, setError]     = useState('');
  const [isRunning, setRunning] = useState(false);

  const fileInputRef = useRef(null);
  const outputRef    = useRef(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // Translated data derived from t() — these re-derive on every render when lang changes
  const steps = [
    t('videoToScript.step1'),
    t('videoToScript.step2'),
    t('videoToScript.step3'),
  ];

  const subTabs = [
    { id: 'Transcript', label: t('videoToScript.transcript') },
    { id: 'TV Script',  label: t('videoToScript.tvScript') },
  ];

  const TONE_OPTIONS = [
    { value: 'Formal',        label: t('genpage.toneStandard') },
    { value: 'Urgent',        label: t('genpage.toneUrgent') },
    { value: 'Conversational', label: t('genpage.toneConversational') },
  ];

  const MODE_OPTIONS = [
    { id: 'upload', label: t('videoToScript.uploadVideo'), icon: FiUpload },
    { id: 'url',    label: t('videoToScript.youtubeUrl'),  icon: FiYoutube },
  ];

  // ── File handling ───────────────────────────────────────────────────────────

  const ACCEPTED_TYPES = ['mp4', 'mov', 'avi', 'mkv', 'mp3', 'wav', 'm4a'];

  const validateFile = (f) => {
    if (!f) return 'No file selected.';
    const ext = f.name.split('.').pop().toLowerCase();
    if (!ACCEPTED_TYPES.includes(ext)) return `Unsupported format. Accepted: ${ACCEPTED_TYPES.join(', ')}`;
    if (f.size > 200 * 1024 * 1024) return 'File exceeds 200 MB limit.';
    return null;
  };

  const handleFileSelect = (f) => {
    const err = validateFile(f);
    if (err) { setError(err); return; }
    setError('');
    setFile(f);
    setTranscript('');
    setScript('');
    setCompletedSteps(new Set());
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileSelect(f);
  }, []);

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);

  const formatBytes = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  // ── Run ─────────────────────────────────────────────────────────────────────

  const markDone = (stepId) => setCompletedSteps((prev) => new Set([...prev, stepId]));

  const handleRun = async () => {
    if (mode === 'upload' && !file)          { setError(t('videoToScript.selectFile')); return; }
    if (mode === 'url' && !youtubeUrl.trim()) { setError(t('videoToScript.pasteUrl'));   return; }

    setError('');
    setRunning(true);
    setTranscript('');
    setScript('');
    setCompletedSteps(new Set());
    setUploadProgress(0);

    try {
      setCurrentStep('upload');
      let tx = '';

      if (mode === 'upload') {
        const result = await videoService.transcribeUpload(file, (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        });
        markDone('upload');
        setCurrentStep('transcribe');
        tx = result.transcript;
        markDone('transcribe');
      } else {
        setCurrentStep('transcribe');
        markDone('upload');
        const result = await videoService.transcribeUrl(youtubeUrl.trim());
        tx = result.transcript;
        markDone('transcribe');
      }

      setTranscript(tx);
      setCurrentStep('generate');
      const result = await videoService.generateScript(tx, tone.toLowerCase(), 'telugu');
      setScript(result.script || '');
      markDone('generate');
      setSubTab('TV Script');

      setTimeout(() => {
        outputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Something went wrong. Please try again.');
    } finally {
      setRunning(false);
      setCurrentStep(null);
    }
  };

  // ── Regenerate ───────────────────────────────────────────────────────────────

  const handleRegenerate = async () => {
    if (!transcript) return;
    setError('');
    setRunning(true);
    setCurrentStep('generate');
    setCompletedSteps((prev) => { const next = new Set(prev); next.delete('generate'); return next; });
    try {
      const result = await videoService.generateScript(transcript, tone.toLowerCase(), 'telugu');
      setScript(result.script || '');
      markDone('generate');
      setSubTab('TV Script');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Script regeneration failed.');
    } finally {
      setRunning(false);
      setCurrentStep(null);
    }
  };

  // ── Download ─────────────────────────────────────────────────────────────────

  const handleDownload = async (text, titleSuffix, format) => {
    const title = `Video to Script — ${titleSuffix} — ${new Date().toLocaleDateString('en-IN')}`;
    try {
      const blob = await contentService.downloadContent({ text, title, format });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `video-script-${titleSuffix.toLowerCase().replace(/\s+/g, '-')}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (_) {
      setError(t('genpage.downloadError'));
    }
  };

  // ── Prompt section ────────────────────────────────────────────────────────────

  const handleTogglePrompt = async () => {
    if (!promptOpen && !promptData) {
      setPromptLoading(true);
      try {
        const data = await promptService.getPrompt('videoToScript');
        setPromptData(data);
      } catch (_) {
        setPromptData(null);
      } finally {
        setPromptLoading(false);
      }
    }
    setPromptOpen((v) => !v);
  };

  const hasOutput  = transcript || script;
  const isRunnable = !isRunning && (mode === 'upload' ? !!file : !!youtubeUrl.trim());

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">

      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
          <FiVideo size={20} className="text-primary-600" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('videoToScript.title')}</h1>
          <p className="text-sm text-gray-500">{t('videoToScript.desc')}</p>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        {MODE_OPTIONS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => { if (!isRunning) { setMode(id); setError(''); } }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              mode === id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">

        {/* Left: input + controls */}
        <div className="space-y-4">

          {/* Upload mode */}
          {mode === 'upload' && (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => !isRunning && fileInputRef.current?.click()}
              className={`relative w-full border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all flex flex-col items-center justify-center ${
                isDragging
                  ? 'border-primary-400 bg-primary-50'
                  : file
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-300 bg-gray-50 hover:border-primary-300 hover:bg-primary-50/30'
              }`}
              style={{ minHeight: '160px' }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".mp4,.mov,.avi,.mkv,.mp3,.wav,.m4a"
                className="hidden"
                onChange={(e) => { const f = e.target.files[0]; if (f) handleFileSelect(f); }}
                disabled={isRunning}
              />
              {file ? (
                <div className="space-y-1.5">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center mx-auto">
                    <FiVideo size={20} className="text-green-600" />
                  </div>
                  <p className="font-semibold text-sm text-gray-800 truncate max-w-xs mx-auto">{file.name}</p>
                  <p className="text-xs text-gray-500">{formatBytes(file.size)}</p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setTranscript('');
                      setScript('');
                      setCompletedSteps(new Set());
                    }}
                    className="text-xs text-red-500 hover:text-red-700 mt-1"
                  >
                    {t('videoToScript.remove')}
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="w-10 h-10 rounded-xl bg-gray-200 flex items-center justify-center mx-auto">
                    <FiUpload size={20} className="text-gray-500" />
                  </div>
                  <p className="text-sm font-medium text-gray-700">
                    {isDragging ? t('videoToScript.dropping') : t('videoToScript.drop')}
                  </p>
                  <p className="text-xs text-gray-400">{t('videoToScript.fileTypes')}</p>
                </div>
              )}

              {/* Upload progress bar */}
              {isRunning && currentStep === 'upload' && uploadProgress > 0 && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 rounded-b-2xl overflow-hidden">
                  <div
                    className="h-full bg-primary-500 transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              )}
            </div>
          )}

          {/* YouTube URL mode */}
          {mode === 'url' && (
            <div className="space-y-2">
              <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                {t('videoToScript.youtubeUrl')}
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="url"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="input w-full pl-8 text-sm"
                    disabled={isRunning}
                  />
                </div>
              </div>
              <p className="text-[10px] text-gray-400">{t('videoToScript.publicOnly')}</p>
            </div>
          )}

          {/* Tone */}
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{t('videoToScript.tone')}</span>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
                {TONE_OPTIONS.map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => !isRunning && setTone(value)}
                    className={`px-3 py-1.5 font-medium transition-colors ${
                      tone === value ? 'bg-primary-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Generate button */}
          <button
            onClick={handleRun}
            disabled={!isRunnable}
            className="btn btn-primary w-full flex items-center justify-center gap-2"
          >
            {isRunning ? (
              <>
                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t('videoToScript.processing')}
              </>
            ) : (
              <>
                <FiZap size={14} />
                {t('videoToScript.transcribeGenerate')}
              </>
            )}
          </button>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 text-sm text-danger-600 bg-danger-50 border border-danger-200 rounded-xl px-3 py-2.5">
              <FiAlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <span className="flex-1">{error}</span>
              <button onClick={() => setError('')} className="text-danger-400 hover:text-danger-600">
                <FiX size={12} />
              </button>
            </div>
          )}

          {/* Progress steps */}
          {(isRunning || completedSteps.size > 0) && (
            <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 max-w-[500px] mx-auto">
              <StepIndicator currentStep={currentStep} completedSteps={completedSteps} steps={steps} />
            </div>
          )}
        </div>

        {/* Right: output */}
        <div ref={outputRef} className="space-y-4">

          {/* Empty state */}
          {!hasOutput && !isRunning && (
            <div className="border border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
              <FiVideo size={40} className="text-gray-200" />
              <p className="text-sm font-medium">{t('videoToScript.emptyTitle')}</p>
              <p className="text-xs text-gray-400 text-center px-8">
                {mode === 'upload' ? t('videoToScript.emptyUpload') : t('videoToScript.emptyUrl')}
              </p>
            </div>
          )}

          {/* Loading */}
          {isRunning && !hasOutput && (
            <div className="border border-gray-200 rounded-2xl flex flex-col items-center justify-center py-16 gap-3 text-primary-600 bg-gray-50">
              <div className="w-10 h-10 border-[3px] border-primary-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm font-semibold">
                {currentStep === 'upload'     && (mode === 'upload' ? t('videoToScript.uploading') : t('videoToScript.fetchingYoutube'))}
                {currentStep === 'transcribe' && t('videoToScript.transcribing')}
                {currentStep === 'generate'   && t('videoToScript.converting')}
              </p>
            </div>
          )}

          {/* Output panels */}
          {hasOutput && (
            <div className="border border-gray-200 rounded-2xl overflow-hidden">
              <SubTabBar active={subTab} onChange={setSubTab} tabs={subTabs} />
              <div className="p-4 space-y-2">
                {subTab === 'Transcript' && (
                  <OutputPanel
                    text={transcript}
                    label={t('videoToScript.rawTranscript')}
                    onDownload={(fmt) => handleDownload(transcript, 'Transcript', fmt)}
                  />
                )}
                {subTab === 'TV Script' && (
                  <OutputPanel
                    text={script}
                    label={t('videoToScript.generatedScript')}
                    onDownload={(fmt) => handleDownload(script, 'TV Script', fmt)}
                  />
                )}
              </div>

              {/* Regenerate */}
              {transcript && (
                <div className="px-4 pb-4 flex items-center gap-2 border-t border-gray-100 pt-3">
                  <button
                    onClick={handleRegenerate}
                    disabled={isRunning}
                    className="btn btn-ghost flex items-center gap-1.5 text-sm text-primary-600 disabled:opacity-40"
                  >
                    <FiRefreshCw size={13} className={isRunning && currentStep === 'generate' ? 'animate-spin' : ''} />
                    {t('videoToScript.regenerate')}
                  </button>
                  <span className="text-xs text-gray-400">{t('videoToScript.regenerateNote')}</span>
                </div>
              )}
            </div>
          )}

          {/* Prompt section */}
          {hasOutput && (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={handleTogglePrompt}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition text-sm font-medium text-gray-700"
              >
                <span className="flex items-center gap-2">
                  <FiCode size={14} className="text-gray-400" />
                  {t('videoToScript.promptUsedFor')}{' '}
                  <span className="text-primary-600">{t('videoToScript.title')}</span>
                </span>
                <FiChevronDown
                  size={14}
                  className={`text-gray-400 transition-transform duration-200 ${promptOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {promptOpen && (
                <div className="px-4 py-3 space-y-3 border-t border-gray-200 bg-white">
                  {promptLoading ? (
                    <p className="text-xs text-gray-400">{t('genpage.loadingPrompt')}</p>
                  ) : promptData ? (
                    <>
                      <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5">
                          {t('genpage.userPromptTemplate')}
                        </p>
                        <pre className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed border border-gray-100">
                          {promptData.userPromptTemplate || t('genpage.noTemplate')}
                        </pre>
                      </div>
                      <div className="flex items-start justify-between gap-3">
                        {promptData.notes && (
                          <p className="text-xs text-gray-500 italic flex-1">{promptData.notes}</p>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => navigate('/settings/prompt-editor?type=videoToScript')}
                            className="flex-shrink-0 flex items-center gap-1 text-xs text-primary-600 hover:underline font-medium"
                          >
                            <FiEdit3 size={11} />
                            {t('genpage.editPrompt')}
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-gray-400">{t('genpage.promptError')}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
